import { useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import InsightContactDrawer from '../../components/InsightContactDrawer';
import { preProcessRequestData, preProcessSubmitData } from '../../lib';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

const FORM_ID = 'INSIGHT_CONTACT_EDIT_FORM';

function InsightContactEdit({ open, onClose, editContact, commonParams }) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put } = useDataProvider(provider);

  const fetchDefaultValues = useCallback(async () => {
    return {
      data: {
        ...preProcessRequestData(editContact),
        insightPurchased: commonParams.insightPurchased,
      },
      errors: null,
    };
  }, [editContact, commonParams.insightPurchased]);

  const onSubmit = async data => {
    try {
      const response = await put(
        `${commonParams.phonesUrl}/${editContact.id}`,
        preProcessSubmitData(data),
      );

      if (response?.ok) {
        const warnings = response.data?.warnings;
        const hasWarnings = Array.isArray(warnings) && warnings.length > 0;

        if (hasWarnings) {
          snackbar.showMessage({
            type: 'warning',
            data: formatMessage(commonParams.phonesSaveWarning),
          });
        } else {
          snackbar.showMessage(formatMessage(commonParams.phonesSaveSuccess));
        }

        return {
          nextValues: {
            ...preProcessRequestData(response?.data),
            insightPurchased: commonParams.insightPurchased,
          },
          errors: null,
          callback: () => {
            commonParams.refreshTable();
            onClose();
          },
        };
      }

      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });

      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  return (
    <InsightContactDrawer
      formId={FORM_ID}
      formRef={formRef}
      title={formatMessage({
        id: 'EDIT_CONTACT',
        defaultMessage: 'Edit contact',
      })}
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      schema={editSchema}
      validationSchema={validationSchema(formatMessage)}
      fetchDefaultValues={fetchDefaultValues}
    />
  );
}

export default observer(InsightContactEdit);
